import { useQuery } from "@tanstack/react-query";
import { api, generateQueryKey } from "@/lib/api";
import type { Activity } from "./use-account-activities";
import type { Opportunity } from "./use-account-opportunities";

export interface PersonFragment {
  id: string;
  external_identifier: string;
  identifier_type: string;
  identifier_external_record_type: string | null;
  external_record_created_at: string | null;
  first_name: string | null;
  last_name: string | null;
  email_address: string | null;
  job_title: string | null;
}

export interface Person {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email_address: string | null;
  job_title: string | null;
  fragments: PersonFragment[];
  is_primary: boolean | null;
  primary_contact_for_opp_ids: string[] | null;
  inboundTouchpoints?: number;
  outboundTouchpoints?: number;
}

export function useAccountPersons(accountId?: string) {
  const {
    data: persons = [],
    isLoading,
    error,
    ...rest
  } = api.useQuery<Person[]>(`datahub/accounts/${accountId}/persons/`, {
    enabled: !!accountId,
  });

  const getPersonById = (personId: string) =>
    persons.find((person) => person.id === personId);

  const getPersonName = (personId: string) => {
    const person = getPersonById(personId);
    if (!person) return personId;
    const name = [person.first_name, person.last_name]
      .filter(Boolean)
      .join(" ");
    return name || person.email_address || personId;
  };

  const getPersonEmail = (personId: string) => {
    const person = getPersonById(personId);
    if (!person) return "";
    return person.email_address || "";
  };

  // Search through list of persons to find a hubspot contact id
  // return the first one found
  const getPersonHubspotContactId = (activity: Activity | null) => {
    if (!activity?.people?.length) return "";
    // Search through activity people and find the first one with a Hubspot contact ID
    for (const activityPerson of activity.people) {
      const person = getPersonById(activityPerson.id);
      if (!person) continue;

      const hubspotFragment = person.fragments.find(
        (fragment) =>
          fragment.identifier_external_record_type === "hubspot.contact",
      );

      if (hubspotFragment?.external_identifier) {
        return hubspotFragment.external_identifier;
      }
    }
    return "";
  };

  return {
    persons,
    getPersonById,
    getPersonName,
    getPersonEmail,
    getPersonHubspotContactId,
    isLoading,
    error,
    ...rest,
  };
}

export function useAccountPersonsByOpportunities(
  accountId?: string,
  opportunityIds?: string[],
) {
  // Fetch all persons for the account
  const {
    data: allPersons = [],
    isLoading: isLoadingAllPersons,
    error: allPersonsError,
    ...allPersonsRest
  } = api.useQuery<Person[]>(`datahub/accounts/${accountId}/persons/`, {
    enabled: !!accountId,
  });

  // Fetch opportunities to get contacts
  const queryKey = opportunityIds?.length
    ? [
        ...generateQueryKey(`datahub/accounts/${accountId}/opportunities`),
        { opportunity_ids: opportunityIds },
      ]
    : generateQueryKey(`datahub/accounts/${accountId}/opportunities`);

  const {
    data: opportunities = [],
    isLoading: isLoadingOpportunities,
    error: opportunitiesError,
  } = useQuery<Opportunity[]>({
    queryKey,
    queryFn: () => {
      const params: Record<string, string> = {};
      if (opportunityIds?.length) {
        params.opportunity_ids = opportunityIds.join(",");
      }

      return api.get<Opportunity[]>(
        `datahub/accounts/${accountId}/opportunities`,
        { params },
      );
    },
    enabled: !!accountId && opportunityIds !== undefined,
  });

  // Filter persons based on opportunities
  const filteredPersons = opportunityIds?.length
    ? allPersons.filter((person) => {
        // If no opportunities are selected, show all persons
        if (!opportunityIds.length) return true;

        // Check if this person is a contact in any of the selected opportunities
        const isContact = opportunities.some((opp: Opportunity) =>
          opp.contacts.some((contact) => contact.id === person.id),
        );

        return isContact;
      })
    : allPersons;

  const getPersonById = (personId: string) =>
    filteredPersons.find((person) => person.id === personId) ||
    allPersons.find((person) => person.id === personId);

  const getPersonName = (personId: string) => {
    const person = getPersonById(personId);
    if (!person) return personId;
    const name = [person.first_name, person.last_name]
      .filter(Boolean)
      .join(" ");
    return name || person.email_address || personId;
  };

  const getPersonEmail = (personId: string) => {
    const person = getPersonById(personId);
    if (!person) return "";
    return person.email_address || "";
  };

  return {
    persons: filteredPersons,
    allPersons,
    getPersonById,
    getPersonName,
    getPersonEmail,
    isLoading: isLoadingAllPersons || isLoadingOpportunities,
    error: allPersonsError || opportunitiesError,
    ...allPersonsRest,
  };
}
